/* eslint max-classes-per-file: ["error", 2] */

'use strict';

define('vb/private/utils',[
  'knockout',
  'vb/private/constants',
  'vb/errors/httpError',
  'jsondiff',
  'vbc/private/utils',
  'vb/private/configuration',
  'urijs/URI',
], (ko, Constants, HttpError, JsonDiff, CommonUtils, Configuration, URI) => {
  let logger;

  // Because of circular dependency we need to load the logger on demand
  const getLogger = () => {
    if (!logger) {
      const Log = requirejs('vb/private/log');
      logger = Log.getLogger('/vb/private/utils');
    }

    return logger;
  };

  // Use jsonDiff to compare state object
  const jsonDiff = JsonDiff.create({
    arrays: {
      detectMove: false,
    },
    cloneDiffValues: false,
  });

  class NameFilters {
    static isNotDecorator(name) {
      return name && !name.startsWith(Constants.Decorators.PREFIX);
    }
  }

  const BASE_URL_PARTS = ['VB_SERVER', 'CONTEXT_ROOT', 'APP_URL_PREFIX', 'APP_ID', 'APP_VERSION'];

  class Utils extends CommonUtils {
    /**
     * Return a promise to load a resource.
     * Reject with the error if there was an error or the file doesn't exist.
     *
     * @param  {String} resource the path to the resource to load
     * @return {Promise} a promise resolving to the content of the resource
     */
    static getResource(resource) {
      return new Promise((resolve, reject) => {
        requirejs([resource],
          (loaded) => {
            resolve(loaded);
          },
          (reason) => {
            let error;
            // For HTTP error, the requireType property is undefined
            if (reason.requireType) {
              error = reason;
            } else {
              error = new HttpError(reason.xhr.status, reason);
            }

            reject(error);
          });
      });
    }

    /**
     * Return a promise to load an array of resources.
     * Reject with error if there was an error or the resources don't exist.
     *
     * @param resources an array of paths to the resources to load
     * @returns {Promise} a promise to return an array of loaded resources
     */
    static getResources(resources) {
      return new Promise((resolve, reject) => {
        requirejs(resources,
          (...loaded) => {
            resolve(loaded);
          },
          (reason) => {
            if (reason.xhr && reason.xhr.status) {
              reject(new HttpError(reason.xhr.status, reason));
            } else {
              reject(reason);
            }
          });
      });
    }

    /**
     * Check if a resource exists at the given path.
     * @param  {String} resource the path to the resource to check
     * @return {Promise}         a Promise that resolves to true if the file exist
     */
    static resourceExists(resource) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        const res = `${require.toUrl('')}${resource.substr(5)}`;

        xhr.open('HEAD', res);
        xhr.onload = function () {
          resolve(this.status !== 404);
        };
        xhr.onerror = function () {
          reject({ status: this.status, statusText: xhr.statusText });
        };
        xhr.send();
      });
    }

    static getTextResource(resource) {
      return Utils.getResource(`text!${resource}`);
    }

    /**
     * A JSON parser that throws an Error with the resource path.
     * @param  {String} jsonContent the JSON content of the resource
     * @param  {String} resource the path of the resource being parsed
     * @return {Object} the object
     */
    static parseJsonResource(jsonContent, resource = 'Unknown resource') {
      // An undefined, null or empty content is treated as a error. It's the case
      // for Android where no error is thrown but the content is empty when a file
      // is not found.
      if (!jsonContent) {
        throw new HttpError(404, new Error(`Empty file content for ${resource}.`));
      }

      try {
        return JSON.parse(jsonContent);
      } catch (error) {
        getLogger().error('Error when parsing', resource, error);
        throw (error);
      }
    }

    /**
     * Load a resource and parse it.
     * If the result of loading is falsy (undefined, null or '') it is not valid
     * and will throw an Http error.
     * If there is a parsing error, this method logs the name
     * of the resource failing and rethrow the error.
     *
     * @param  {String} resource the path to the resource
     * @return {Promise.<Object>}  a promise to the model object.
     */
    static loadAndParse(resource) {
      return Utils.getTextResource(resource).then((jsonContent) => Utils.parseJsonResource(jsonContent, resource));
    }

    // dynamically load and create the runtime environment, and cache the Promise
    static getRuntimeEnvironment() {
      if (!Utils.runtimeEnvironmentPromise) {
        Utils.runtimeEnvironmentPromise = this.getResource('vbRuntimeEnvironmentClass')
          .then((RuntimeEnvironmentClass) => new RuntimeEnvironmentClass());
      }
      return Utils.runtimeEnvironmentPromise;
    }

    /**
     * Will clone the object, but will copy primitive values, and objects
     * that are not directly derived from an object. For example, objects
     * whose parent class is not object will not be cloned, but passed by
     * reference.
     *
     * @param destination
     * @param source
     * @returns {*}
     */
    static cloneObject(source, destination) {
      let target = destination;

      // determine what to do if destination is not specified
      if (typeof destination === 'undefined') {
        if (!Utils.isCloneable(source)) {
          return source;
        }

        // otherwise create the right target
        target = Array.isArray(source) ? [] : {};
      }

      for (const name in source) {
        const copy = source[name];
        const src = target[name];
        let clone;

        // Prevent never-ending loop
        if (target === copy) {
          return target;
        }

        // Recurse if we're merging plain objects or arrays

        const copyIsArray = Array.isArray(copy);
        if (Utils.isCloneable(copy)) {
          if (copyIsArray) {
            // always clone into an empty array instead of overwriting the original
            clone = [];
          } else {
            clone = src && Utils.isObject(src) ? src : {};
          }

          // Never move original objects, clone them
          target[name] = Utils.cloneObject(copy, clone);

          // Don't bring in undefined values
        } else {
          target[name] = copy;
        }
      }

      return target;
    }

    /**
     * Empties all arrays in the value. This will return the new value, but it will also mutate
     * the value passed in.
     *
     * @param value a value, can be a primitive, object, or array
     * @param preserveNonEmpty
     */
    static emptyArrays(value, preserveNonEmpty = false) {
      if (!value) {
        return value;
      }
      let newValue = value;

      if (Array.isArray(value)) {
        newValue = [];
        if (preserveNonEmpty && !Utils.isStructureEmpty(value)) {
          Object.keys(value).forEach((k) => {
            const v = value[k];
            newValue.push(Utils.emptyArrays(v, preserveNonEmpty));
          });
        }
      } else if (Utils.isObject(value)) {
        Object.keys(value).forEach((key) => {
          const val = value[key];
          newValue[key] = Utils.emptyArrays(val, preserveNonEmpty);
        });
      }

      return newValue;
    }

    /**
     * Determines if an array has any set values on it's immediate properties.
     *
     * @private
     * @param structure
     */
    static isStructureEmpty(structure) {
      if (structure === undefined) {
        return true;
      }

      if (Array.isArray(structure)) {
        if (structure.length > 1) {
          return false;
        }

        const someChildrenHaveStructure = structure.some((v) => !Utils.isStructureEmpty(v));
        return !someChildrenHaveStructure;
      }

      if (Utils.isObject(structure)) {
        const keys = Object.keys(structure);
        for (let i = 0; i < keys.length; i += 1) {
          const key = keys[i];
          const val = structure[key];
          if (!Utils.isStructureEmpty(val)) {
            return false;
          }
        }
        return true;
      }

      return false;
    }

    static isPrototypeOfObject(test) {
      try {
        return test && (Object.getPrototypeOf(test) === Object.prototype || test.prototype === null);
      } catch (e) {
        // do nothing, happens on IE11 for non-objects
      }
      return false;
    }

    /**
     * Resolves every property of the object.
     * Also freeze the object at every level if the freeze option is set.
     *
     * @param {*} obj the object to traverse and resolve expression.
     * @param {Object} options object with only one possible property for now, freeze
     * { freeze: true } (the default) indicate all nested object should be frozen.
     * @returns {*}
     */
    static deepResolve(obj, options = { freeze: true }) {
      if (Utils.isObjectOrArray(obj)) {
        const propNames = Object.keys(obj); // don't use getOwnPropertyNames(); causes recursion on 'constants'

        propNames.forEach((name) => {
          obj[name] = Utils.deepResolve(obj[name], options);
        });

        return (options && options.freeze === true) ? Object.freeze(obj) : obj;
      }

      return Utils.resolveIfObservable(obj);
    }

    /**
     * Given a value that can be an observable, return the value by resolving the potential observable.
     * @param  {Object|Function} value
     * @return {*}
     */
    static resolveIfObservable(value) {
      return ko.isObservable(value) ? value() : value;
    }

    /**
     * Returns true if value is a primitive.
     *
     * @param value
     * @returns {boolean}
     */
    static isPrimitive(value) {
      return value !== Object(value);
    }

    /**
     * Returns true if the value is defined, non-null, and an object or array.
     *
     * @param value The value to test
     * @returns {boolean} True if the object is defined, non-null, and an object or array
     */
    static isObjectOrArray(value) {
      return value && typeof value === 'object';
    }

    /**
     * Return true if the value (or the actual instance) is of an extended type.
     * @param  {Object}  value
     * @return {Boolean}
     */
    static isExtendedType(value) {
      return (value && typeof value.isExtendedType === 'function' && value.isExtendedType());
    }

    /**
     * Returns true if the type declaration is a instance factory type. Factory types are also automatically
     * instance types. See isInstanceType.
     * instanceFactory types can be declared in 2 ways when used under types -
     * vb/InstanceFactory<foo/bar>, where foo/bar is an instance type (must have a '/')
     * vb/InstanceFactory, where the instance type is the same as the name of the type. example
     * "types": {
     *   "foo/bar": {
     *     "constructorType": "vb/InstanceFactory"
     *   }
     * }
     * @param typeDef the type definition that is typically defined under types
     */
    static isTypeDefInstanceFactory(typeDef) {
      return (typeof typeDef === 'string' && typeDef.indexOf(Constants.VariableTypePrefixes.INSTANCE_FACTORY) === 0);
    }

    /**
     * Returns whether or not the type is an instance (class) type, e.g., vb/ServiceDataProvider.
     *
     * @param type the type to check
     * @returns {boolean}
     */
    static isInstanceType(type) {
      // types are referenced within the same container in the following format:
      // <scope>:<name>
      // types are referenced as follows in the extensions:
      // <'base'|''|extensionId>/<scope>:<name>
      // so the actual type comes after the colon
      // for simple types without the scope, check the whole string
      return typeof type === 'string' && type.substring(type.indexOf(':') + 1).indexOf('/') >= 0;
    }

    /**
     * Returns true if the type is an object type, e.g., { "prop": "string" }.
     * Note that this will return true for the wildcard "object" type and false for the wildcard "any" type.
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isObjectType(type) {
      return typeof type === 'string' ? type === 'object' : Utils.isObject(type);
    }

    /**
     * Returns true if the type is an array type, e.g., string[] or [{"prop": "string"}].
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isArrayType(type) {
      return typeof type === 'string' && type.endsWith('[]') ? true : Array.isArray(type);
    }

    /**
     * Returns true if type is a primitive type, i.e., string, number or boolean.
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isPrimitiveType(type) {
      return ['string', 'number', 'boolean'].some((primitiveType) => type === primitiveType);
    }

    /**
     * Returns true if the type is an object or array type.
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isObjectOrArrayType(type) {
      return Utils.isObjectType(type) || Utils.isArrayType(type);
    }

    /**
     * Return true if the type is any or object wildcard type.
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isWildcardType(type) {
      return type === 'any' || type === 'object';
    }

    /**
     * Returns true if the type is the wildcard "any" type.
     *
     * @param type the type to test
     * @returns {boolean}
     */
    static isAnyType(type) {
      return type === 'any';
    }

    /**
     * Returns the row type of an array type and null if the type is not an array type.
     *
     * @param type the type the extract the row type
     * @returns {*}
     */
    static getArrayRowType(type) {
      if (Utils.isArrayType(type)) {
        return Array.isArray(type) ? type[0] : type.substring(0, type.length - 2);
      }

      return null;
    }

    /**
     * Return true if the value is cloneable which means it is an array or an object whose prototype is
     * Object.prototype.
     *
     * @param value The value to test
     * @returns {boolean} True if the value is cloneable
     */
    static isCloneable(value) {
      // original expression
      // !(!Utils.isObjectOrArray(value) || (!Array.isArray(value) && !Utils.isPrototypeOfObject(value)));
      return Utils.isObjectOrArray(value) && (Array.isArray(value) || Utils.isPrototypeOfObject(value));
    }

    /**
     * Retrieve the name of an item to use in a web storage.
     * @param  {string} applicationId
     * @param  {string} scopeName    the name of scope for this variable
     * @param  {string} namespace    the namespace of the variable
     * @param  {string} name         the name of the variable
     * @return {string}              the item name
     */
    static getWebStorageItemName(applicationId, scopeName, namespace, name) {
      return `orcl.vbcs.${applicationId}.${scopeName}.${namespace}.${name}`;
    }

    /**
     * Replace the current URL pathname without changing anything else on the URL.
     *
     * @param  {String} pathname the pathname to replace the current one
     * @return {String}          the new URL
     */
    static replaceUrlPathname(pathname) {
      // TODO: come up with a better approach to detect capabilities based on running platform
      if (!this.isMobile()) {
        // Use the anchor element trick to rebuild the full href by only replacing
        // pathname.
        const parser = document.createElement('a');
        parser.href = window.location.href;
        parser.pathname = pathname;
        window.history.replaceState(window.history.state, '', parser.href);

        return parser.href;
      }

      return pathname;
    }

    static diff(obj1, obj2) {
      return jsonDiff.diff(obj1, obj2);
    }

    /**
     * Changes the browser history state and URL. In case the browser is throttling
     * updates to the history (like chrome does) repeat attempt to change the history every
     * second until it is correctly applied.
     * @param  {Object} state
     * @param  {String} url
     * @param  {String} op    'replaceState' or 'pushState'
     * @return {Promise} a promise that resolves when the browser history is changed
     */
    static changeBrowserState(state, url, op) {
      // Do the push or replace
      window.history[op](state, '', url);
      // Check if it succeeded and retry after a second if not
      if (url !== window.location.href || this.diff(state, window.history.state)) {
        // logger.info('Failed changing browser state because of browser throttling, trying again in 1s.');
        return new Promise((resolve) => {
          window.setTimeout(() => {
            resolve();
          }, 1000);
        }).then(() => this.changeBrowserState(state, url, op));
      }

      return Promise.resolve();
    }

    static appendToUrlPathname(segment) {
      const pathname = window.location.pathname;
      const sep = (pathname.slice(-1) === Constants.PATH_SEPARATOR) ? '' : Constants.PATH_SEPARATOR;

      this.replaceUrlPathname(`${pathname}${sep}${segment}`);
    }

    /**
     * Promise rejects on completion of setTimeout, message will be 'timeout'.
     * @param millisecs
     * @param error optional, will reject with Error('timeout') otherwise
     * @returns {Promise}
     */
    static getTimeoutPromise(millisecs, error) {
      return new Promise((_, reject) => {
        const e = error || new Error('timeout');
        setTimeout(() => reject(e), millisecs);
      });
    }

    /**
     * @param promiseOrValue typically, a promise, but can be any value (value will cause this to resolve immediately).
     * @param millisecs timeout length
     * @param errorObj optional an Error object that will be passed to reject(), and subsequently the catch() handler
     * @returns {Promise.<*>|Promise<R>}
     */
    static promiseRaceWithTimeout(promiseOrValue, millisecs, errorObj = new Error('promise timed out')) {
      const timeoutPromise = Utils.getTimeoutPromise(millisecs, errorObj);
      return Promise.race([promiseOrValue, timeoutPromise]);
    }

    /**
     * Take a load error, extract the status and build an error message.
     * @param  {Object} err an error thrown by Utils.getResource
     * @return {string} the formatted error message.
     */
    static formatLoadError(err) {
      const errInfo = { error: err };

      if (!err || !err.message) {
        errInfo.message = 'Unknown error';
      } else {
        errInfo.message = err.message;
        // In case of http error, remove the file URL in front of status
        if (err.statusCode) {
          const statusIndex = err.message.indexOf('status: ');
          if (statusIndex >= 0) {
            const status = err.message.substring(statusIndex);
            errInfo.message = `HTTP error ${status}`;
          }
        }
      }

      return errInfo.message;
    }

    /**
     * @returns {boolean} true if browser is Safari
     */
    static isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    /**
     * Determines whether the application is running in a mobile Safari browser.
     *
     * @returns {boolean}
     */
    static isMobileSafari() {
      return /mobile\/.*safari/i.test(navigator.userAgent);
    }

    /**
     * Reads a given file as binary Blob.
     * @param filePath file path to a file to read
     * @param mimeType optional, will use filename ext otherwise, defaults to application/octet-stream
     */
    static readBlob(filePath, mimeType) {
      const type = mimeType || Utils.getMimeType(filePath);
      const options = type ? { type } : undefined;

      if (!Utils.isMobile()) {
        return fetch(filePath)
          .then((response) => {
            if (response.ok) {
              return response.blob();
            }
            return null;
          });
      }

      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(filePath, (fileEntry) => {
          fileEntry.file((file) => {
            const reader = new FileReader();

            reader.onloadend = () => {
              resolve(new Blob([new Uint8Array(reader.result)], options));
            };

            reader.readAsArrayBuffer(file);
          }, function (e) {
            reject(e);
          });
        }, function (e) {
          reject(e);
        });
      });
    }

    /**
     * for now, a limited mapping of common image types, by file extension
     * returns application/octet-stream if not one of the few known types.
     *
     * @param filePath
     * @returns {*}
     */
    static getMimeType(filePath) {
      const ext = filePath.substr(filePath.lastIndexOf('.') + 1);
      switch (ext) {
        case 'jpe':
        case 'jpeg':
        case 'jpg': return 'image/jpeg';

        case 'svg': return 'image/svg+xml';

        case 'bmp': return 'image/bmp';

        case 'gif': return 'image/gif';

        default:
      }

      return 'application/octet-stream';
    }

    /**
     * @typedef {Object} Version
     * @property {number} major major portion of the version
     * @property {number} minor minor portion of the version
     * @property {number} patch patch portion of the version
     */
    /**
     * parses a version string and returns its parts
     * @param versionStr
     * @returns {Version} Parsed version object
     */
    static parseVersionString(versionStr) {
      if (typeof (versionStr) !== 'string') {
        return null;
      }

      const versionParts = versionStr.split('.');

      // parse from string or default to 0 if can't parse
      return {
        major: parseInt(versionParts[0], 10) || 0,
        minor: parseInt(versionParts[1], 10) || 0,
        patch: parseInt(versionParts[2], 10) || 0,
      };
    }

    /**
     * Utility function to check if iOS version is >= 11.3 and < 12 for local file:// href. Used to check for ios bug.
     * https://bugs.webkit.org/show_bug.cgi?id=183028.
     *
     * @returns {boolean} true if iOS version is at least 11.3 and < 12 else false
     */
    static isLocalIOSVersionAtLeast113AndLessThan12() {
      // currently VB only supports Cordova with file:// protocol. So no need to check for file:// in href
      if (Utils.isIos() && window.device) {
        // we include the device plugin by default, so we can use version from device plugin
        // JIT initialize version only once
        Utils.version = Utils.version || Utils.parseVersionString(window.device.version);
        const { version } = Utils;

        if (version && (version.major === 11 && version.minor >= 3) && (version.major < 12)) {
          return true;
        }
      }

      return false;
    }

    /**
     * Deletes decorator properties form the object.
     * @param obj
     * @param filterFnc optional
     * @returns {*}
     */
    static removeDecorators(obj, filterFnc = NameFilters.isNotDecorator) {
      if (obj) {
        const o = obj;
        Object.keys(obj || {}).forEach((name) => {
          if (!filterFnc(name)) {
            delete o[name];
          }
        });
      }
      return obj;
    }

    /**
     * Flattens a nested object into a an object with one level of properties, with dots in the property names.
     * example:
     *   {
     *    one: {
     *      subone: {
     *        suboneone: 'x',
     *        subonetwo: 'y'
     *      }
     *    },
     *    two: {
     *      subtwo: 'z'
     *    }
     *   }
     * result:
     * {
     *   one.subone.suboneone: 'x',
     *   one.subone.subonetwo: 'y',
     *   two.subtwo: 'z'
     * }
     * @param object
     * @returns {*}
     */
    static flatten(object) {
      return Object.assign({}, ...(function _flatten(subobj, path = '') {
        if (subobj) {
          return [].concat(
            ...Object.keys(subobj).map((key) =>
              (typeof subobj[key] === 'object' ? _flatten(subobj[key], `${path}${path ? '.' : ''}${key}`) :
                ({ [`${path}${path ? '.' : ''}${key}`]: subobj[key] }))));
        }
        return [];
      }(object)));
    }

    /**
     * Converts File object into Blob.
     *
     * Used as a workaround for some Chrome request-cloning bug in Chrome service workers.
     *
     * @param file - file to be converted into Blob.
     * @returns (Blob} a blob duck typed as File.
     */
    static fileToBlob(file) {
      return new Blob([file], { // Duck Blob as File
        type: file.type,
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        lastModifiedDate: file.lastModifiedDate,
      });
    }

    /**
     * Returns the 'base' for the proxy/tokenRelay URLs.
     *
     * Use the vbInitConfig parts, so this works for 'regular' and 'vanity url" use cases.
     * examples:
     *
     * https://vbmasterdev-vbcsqatest.uscom-central-1.c9dev1.oc9qadev.com/ic/builder/design/myapp/1.0;profile=myprof
     * https://vbmasterdev-vbcsqatest.uscom-central-1.c9dev1.oc9qadev.com:443/ic/builder/rt/somewebapp/live/
     *
     * @param activeProfile
     *
     * @returns {string}
     */
    static getBaseUrlForProxy(activeProfile = null) {
      const vbInitConfig = window.vbInitConfig || {};

      let url = BASE_URL_PARTS.reduce((acc, curr) => {
        let segment = vbInitConfig[curr];
        // BUFP-30798: include 'profile' as matrix param for proxy/token-relay URLs
        // encode it, just in case, but it shouldn't be necessary
        if (curr === 'APP_VERSION' && activeProfile) {
          segment = `${segment};profile=${encodeURIComponent(activeProfile)}`;
        }
        return `${acc}${segment ? segment + Constants.PATH_SEPARATOR : ''}`;
      }, '');

      if (url && url !== '/////') {
        // remove the leading '/' from the reduce,
        if (url.startsWith('/')) {
          url = url.substring(1);
        }
        // and replace multiple forward slashes with a single slash (except after ':'), and remove ending slash
        return url.replace(/([^:]\/)\/+/g, '$1').replace(/\/+$/, '');
      }

      /**
       * if for some reason, we didn't get nuggets, fall back to using the Configuration.applicationUrl.
       * but we should ALWAYS get nuggets though.
       */
      url = Configuration.applicationUrl || '';

      // and remove the end part that we don't need for the token relay/proxy URLs
      // look for the last two parts, just to be safe
      const endOfBase = ['APP_ID', 'APP_VERSION']
        .reduce((acc, cur) => `${acc}${vbInitConfig[cur]}${vbInitConfig[cur] ? '/' : ''}`, '');
      const index = url.indexOf(endOfBase);
      if (index >= 0) {
        url = url.substring(0, index + endOfBase.length);
      }

      if (activeProfile && vbInitConfig.APP_VERSION) {
        const verLen = vbInitConfig.APP_VERSION.length;
        const verIndex = url.lastIndexOf(vbInitConfig.APP_VERSION);
        if (verIndex >= 0) {
          url = `${url.substring(0, verIndex)}${vbInitConfig.APP_VERSION}`
            + `;profile=${encodeURIComponent(activeProfile)}${url.substring(verIndex + verLen)}`;
        }
      }
      // same as above; remove the leading from the reduce and concat
      // and replace multiple forward slashes with a single slash (except after ':')
      url = url.replace(/([^:]\/)\/+/g, '$1');
      if (url.endsWith(Constants.PATH_SEPARATOR)) {
        url = url.substring(0, url.length - 1);
      }
      return url;
    }

    /**
     * Simple file extension removal.
     * @param fileName
     * @returns {string}
     */
    static removeFileExtension(fileName) {
      const fileParts = fileName.split('.');
      if (fileParts.length > 1) {
        fileParts.pop();
      }
      return fileParts.join('.');
    }

    /**
     * Simple file name removal.
     * @param fullPath
     * @returns {string}
     */
    static removeFileName(fullPath) {
      return URI(fullPath).filename('').toString();
    }

    /**
     * Create a new object, with accessors, that call the functions in the wrapped object.
     *
     * One use-case is to call the JET string functions, with no args, without needing to use a function expression,
     * to match the existing syntax behavior;
     *   ex.  $page.translations.foo.myString calls the JET <strings module>.myString function.
     *
     * @param functions {object} contains ONLY properties that are functions
     * @return {object} contains a set of properties with the same names as 'functions', where obj.x is functions.x()
     *
     * @private
     */
    static createFunctionWrappers(functions) {
      const getters = {};

      Object.keys(functions)
        .forEach((fncName) => {
          Object.defineProperty(getters, fncName, {
            // pass an empty object, to prevent exception; the generated functions don't guard against it.
            get: () => functions[fncName]({}),
          });
        });

      return getters;
    }

    /**
     * Utility to take hyphenated strings/filenames, remove the hyphens, and camel-case it.
     * ex: data-description-overlay.json => dataDescriptionOverlay
     *
     * @param hyphenatedString
     * @returns {string}
     */
    static hyphenatedToCamelCase(hyphenatedString) {
      // in cse its a file, remove any dot-something at the end, for convenience.
      const cleanedString = Utils.removeFileExtension(hyphenatedString);
      const parts = cleanedString.split('-')
        .map((part, index) => (index ? part.charAt(0).toUpperCase() + part.slice(1) : part));
      return parts.join('');
    }

    /**
     * Flattens the specified arguments producing an array with unique, defined elements. The order of the resulting
     * array is the order that the arguments are traversed.
     *
     * @example:
     * console.log(Utils.toFlatUniqueArray(1, 2, 1, 2, [1, [[1, undefined], 2, 3, [4]]])) // outputs [1, 2, 3, 4]
     *
     * @param args
     * @return {*[]}
     */
    static toFlatUniqueArray(...args) {
      // set accumulates the elements that are returned
      // arraySet avoids endless recursion if 2 arrays contain each other
      const doit = (set, arraySet, array) => {
        array.forEach((value) => {
          if (Array.isArray(value)) {
            const aSet = arraySet || new Set();
            if (!aSet.has(value)) {
              aSet.add(value);
              doit(set, aSet, value);
            }
          } else if (value !== undefined) {
            set.add(value);
          }
        });
        return set;
      };

      return [...doit(new Set(), undefined, args)];
    }

    /**
     * Parses the specified string value to return an object with one or more of the following properties: 'prefix',
     * 'main', and 'suffix'. The return is undefined if 'value' is not a string or if no properties were set.
     *
     * The value is parsed as follows:
     * <ul>
     *   <li>If value has a 'suffix' token, the part after the token is the suffix and value now is only
     *   the part before the token.</li>
     *   <li>If value has a 'prefix' token, the part before the token is the prefix and value now is the part
     *   after the token.</li>
     *   <li>main is set to value</li>
     * </ul>
     *
     * Notice that a consequence of the above approach is that if the prefix has the 'suffix token', the
     * returned object has only the main and suffix token - in other words, the prefix cannot have the 'suffix token'.
     *
     * Clients can override the 'prefixToken' and 'suffixToken' with default to ':' and '/' respectively. Moreover,
     * setting a token to an empty string causes it to be ignored - so setting 'prefixToken' causes the prefix
     * handling to be ignored.
     *
     * @examples:
     * console.log(Utils.parseQualifiedIdentifier('p:m/s'));  // { prefix:p, main:m, suffix:s }
     * console.log(Utils.parseQualifiedIdentifier('m/s'));  // { main:m, suffix:s }
     * console.log(Utils.parseQualifiedIdentifier('m'));  // { main:m }
     * console.log(Utils.parseQualifiedIdentifier('/s'));  // { suffix:s }
     * console.log(Utils.parseQualifiedIdentifier('p:'));  // { prefix:p }
     * console.log(Utils.parseQualifiedIdentifier('p/:'));  // { main:p, suffix:: }
     *
     * @param {string} value - the value to be parsed
     * @param {string} [prefixToken] - the prefix token, defaults to <i>:</i>
     * @param {string} [suffixToken] - the suffix token, defaults to <i>/</i>
     * @return {{[prefix]:string, [main]:string, [suffix]:string}|undefined} undefined if value is not a string or the
     *         object described above.
     */
    static parseQualifiedIdentifier(value, { prefixToken = ':', suffixToken = '/' } = {}) {
      if (typeof value === 'string') {
        let prefix;
        let main = '';
        let suffix;

        const suffixIndex = value.indexOf(suffixToken.length > 0 ? suffixToken : null);
        if (suffixIndex >= 0) {
          if (suffixIndex > 0) {
            main = value.substring(0, suffixIndex);
          }
          suffix = value.substring(suffixIndex + 1);
        } else {
          main = value;
        }

        const prefixIndex = main.indexOf(prefixToken.length > 0 ? prefixToken : null);
        if (prefixIndex >= 0) {
          if (prefixIndex > 0) {
            prefix = main.substring(0, prefixIndex);
          }
          main = main.substring(prefixIndex + 1);
        }

        let parsed;
        if (prefix) {
          (parsed || (parsed = {})).prefix = prefix;
        }
        if (main) {
          (parsed || (parsed = {})).main = main;
        }
        if (suffix) {
          (parsed || (parsed = {})).suffix = suffix;
        }
        return parsed;
      }
      return undefined;
    }
  }

  // expose a static instance of filter utilities
  Utils.nameFilters = NameFilters;

  return Utils;
});

